import React, { useMemo } from 'react';
import { TrendingUp, BarChart3, PieChart as PieIcon, Layers } from 'lucide-react';
import { TYPE_COLORS, TYPE_LABELS } from '../../utils/chartTheme';
import {
  ChartCard,
  TrendLineChart,
  HorizontalBarChart,
  StackedBarChart,
  DonutChart
} from '../../utils/chartComponents';

const StatisticsCharts = ({ stats, globalTotals, getManagerInfo }) => {
  // 1. Line chart — trend from globalTotals.by_period
  const trendData = useMemo(() => {
    if (!globalTotals?.by_period?.length) return [];
    return globalTotals.by_period
      .map(p => ({
        date: typeof p.period === 'string' ? p.period.split('T')[0] : p.period,
        count: parseInt(p.count) || 0
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [globalTotals]);

  // 2. Bar chart — managers sorted by total
  const managerData = useMemo(() =>
    stats.map(item => {
      const mgr = getManagerInfo(item);
      const name = mgr.name || `ID: ${item.manager_id?.slice(0, 8)}`;
      return { name, total: item.total_talents || 0 };
    }).sort((a, b) => b.total - a.total)
  , [stats, getManagerInfo]);

  // 3. Stacked bar — managers × types (with conversions)
  const stackedData = useMemo(() =>
    stats.map(item => {
      const mgr = getManagerInfo(item);
      const name = mgr.name || `ID: ${item.manager_id?.slice(0, 8)}`;
      const employee = item.conversions?.to_employee || 0;
      const presale = item.conversions?.to_presale || 0;
      const totalCreated = item.by_type?.candidate || 0;
      const candidate = totalCreated - employee - presale;
      return {
        name,
        candidate: Math.max(candidate, 0),
        employee,
        presale
      };
    }).sort((a, b) =>
      (b.candidate + b.employee + b.presale) - (a.candidate + a.employee + a.presale)
    )
  , [stats, getManagerInfo]);

  // 4. Pie — type distribution (with conversions)
  const pieData = useMemo(() => {
    let totalCandidate = 0;
    let totalEmployee = 0;
    let totalPresale = 0;

    stats.forEach(item => {
      const created = parseInt(item.by_type?.candidate) || 0;
      const toEmployee = item.conversions?.to_employee || 0;
      const toPresale = item.conversions?.to_presale || 0;
      totalEmployee += toEmployee;
      totalPresale += toPresale;
      totalCandidate += Math.max(created - toEmployee - toPresale, 0);
    });

    return [
      { name: TYPE_LABELS.candidate, value: totalCandidate, color: TYPE_COLORS.candidate },
      { name: TYPE_LABELS.employee, value: totalEmployee, color: TYPE_COLORS.employee },
      { name: TYPE_LABELS.presale,  value: totalPresale,  color: TYPE_COLORS.presale }
    ].filter(item => item.value > 0);
  }, [stats]);

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
    return dateStr;
  };

  const STACKED_BARS = [
    { key: 'candidate', name: 'Candidates', color: TYPE_COLORS.candidate },
    { key: 'employee',  name: 'Employees',  color: TYPE_COLORS.employee },
    { key: 'presale',   name: 'Presales',   color: TYPE_COLORS.presale }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* 1. Trend */}
      <ChartCard
        title="Talent Addition Trend"
        icon={TrendingUp}
        hint={trendData.length === 0 ? 'Select a period grouping (day / week / month) in filters to see the trend' : null}
      >
        <TrendLineChart
          data={trendData}
          color={TYPE_COLORS.candidate}
          name="Talents"
          tickFormatter={formatDateLabel}
        />
      </ChartCard>

      {/* 2. Managers by total */}
      <ChartCard
        title="Talents by Manager"
        icon={BarChart3}
        hint={managerData.length === 0 ? 'No data available' : null}
      >
        <HorizontalBarChart
          data={managerData}
          color={TYPE_COLORS.candidate}
          name="Total Talents"
        />
      </ChartCard>

      {/* 3. Stacked by type */}
      <ChartCard
        title="Talents by Manager & Type"
        icon={Layers}
        hint={stackedData.length === 0 ? 'No data available' : null}
      >
        <StackedBarChart data={stackedData} bars={STACKED_BARS} />
      </ChartCard>

      {/* 4. Donut — type distribution */}
      <ChartCard
        title="Talent Type Distribution"
        icon={PieIcon}
        hint={pieData.length === 0 ? 'No data available' : null}
      >
        <DonutChart data={pieData} />
      </ChartCard>
    </div>
  );
};

export default StatisticsCharts;
